import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/layout/Layout';
import ScrollToTop from './components/ScrollToTop';

// Contexts
import { ConsultationProvider, useConsultation } from './context/ConsultationContext';
import { AdminAuthProvider } from './Admin/context/AdminAuthContext';

// Components & Pages
import ConsultFormModal from './components/ConsultFormModal';
import AdminLogin from './Admin/pages/AdminLogin';

import About from './pages/About';
import Services from './pages/Services';
import Projects from './pages/Projects';
import Contact from './pages/Contact';
import Home from './pages/Home';
import Kitchen from './pages/DesignGallery/Kitchen';
import Bedroom from './pages/DesignGallery/Bedroom';
import LivingRoom from './pages/DesignGallery/LivingRoom';
import Bathroom from './pages/DesignGallery/Bathroom';
import Wardrobe from './pages/DesignGallery/Wardrobe';
import LuxuryOffice from './pages/DesignGallery/LuxuryOffice';
import LuxuryInteriors from './pages/DesignGallery/LuxuryInteriors';
import SpaceSaving from './pages/DesignGallery/SpaceSaving';
import LuxuryBuilders from './pages/Services/LuxuryBuilders';
import InteriorDesigning from './pages/Services/InteriorDesigning';
import ArchitecturePlanning from './pages/Services/ArchitecturePlanning';
import CustomerStories from './pages/Discover/CustomerStories';
import Blogs from './pages/Discover/Blogs';
import FAQ from './pages/Discover/FAQ';
import AdminLayout from './Admin/components/AdminLayout';
import Dashboard from './Admin/pages/Dashboard';
import Approvals from './Admin/pages/Team/Approvals';
import Subordinates from './Admin/pages/Team/Subordinates';
import ConsultingQueries from './Admin/pages/Queries/ConsultingQueries';
import ContactEnquiries from './Admin/pages/Queries/ContactEnquiries';
import CreateQuotation from './Admin/pages/Quotations/CreateQuotation';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import AdminProfilePage from './Admin/pages/AdminProfilePage';
import LeadManager from './Admin/pages/LeadManager';
import MaterialRequisition from './Admin/pages/MaterialRequisition';

// Helper Component for Global Modal
const GlobalModal = () => {
  const { isOpen, closeModal, onFormSubmit } = useConsultation();
  return <ConsultFormModal open={isOpen} setOpen={closeModal} onSuccess={onFormSubmit} />;
};

// ==========================================
// 1. UPDATED: Floating WhatsApp with Curved Text
// ==========================================
const FloatingWhatsApp = () => {
  
  const phoneNumber = "917291832000"; 
  const message = "Hello! I am interested in your interior design services.";
  
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-8 right-8 z-50 flex items-center justify-center group"
      aria-label="Chat on WhatsApp"
    >
      {/* === CURVED TEXT SVG === 
         - Positioned absolutely around the icon
         - 'animate-pulse' makes it blink softly
         - 'fill-amber-600' matches your Gold theme
      */}
      <div className="absolute w-28 h-28 -top-4 animate-pulse pointer-events-none">
        <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible">
          {/* Defined path: A semi-circle arc over the top */}
          <path
            id="curve"
            d="M 10,50 A 40,40 0 0,1 90,50" 
            fill="transparent"
          />
          <text className="fill-amber-600 font-bold text-[13px] uppercase tracking-widest drop-shadow-sm">
            <textPath href="#curve" startOffset="50%" textAnchor="middle">
              Chat With Us
            </textPath>
          </text>
        </svg>
      </div>

      {/* WhatsApp Image (Zoom effect on hover) */}
      <img
        src="/whatsapp.png"
        alt="WhatsApp"
        className="w-14 h-14 rounded-full shadow-2xl hover:scale-110 transition-transform duration-300 relative z-10 "
      />
    </a>
  );
};


function App() {
  return (
    <Router>
      <ScrollToTop />

      <Routes>
        
        {/* ======================================================== */}
        {/* 1. ADMIN ROUTES (No Public Layout, No Header/Footer)     */}
        {/* ======================================================== */}
        <Route path="/homes-back/*" element={
          <AdminAuthProvider>
            <Routes>
              <Route path="/" element={<AdminLogin />} />
              <Route element={<AdminLayout />}>
                 <Route path="dashboard" element={<Dashboard />} />
                 <Route path="team/approvals" element={<Approvals />} />
                 <Route path="team/subordinates" element={<Subordinates />} />
                 <Route path="consulting-queries" element={<ConsultingQueries />} />
                 <Route path="contact-enquiries" element={<ContactEnquiries />} />
                 <Route path="quotations/create" element={<CreateQuotation />} />
                 <Route path="lead/lead" element={<LeadManager />} />
                 <Route path="materials" element={<MaterialRequisition />} />
                 <Route path="profile-settings"element={<AdminProfilePage />
          
        } 
      />
                 <Route path="quotations/view" element={<div className="text-white">View Quotations Page</div>} />
              </Route>
            </Routes>
          </AdminAuthProvider>
        } />


        {/* ======================================================== */}
        {/* 2. PUBLIC USER ROUTES (Wrapped in Layout & Context)      */}
        {/* ======================================================== */}
        <Route path="/*" element={
          <ConsultationProvider>
            <Layout>
              
              {/* Global Modal Logic */}
              <GlobalModal /> 
              
              {/* 👇 UPDATED FLOATING WHATSAPP BUTTON 👇 */}
              <FloatingWhatsApp />
              
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/contact" element={<Contact />} />

                {/* Gallery Routes */}
                <Route path="/gallery/kitchen" element={<Kitchen />} />
                <Route path="/gallery/bedroom" element={<Bedroom />} />
                <Route path="/gallery/living-room" element={<LivingRoom />} />
                <Route path="/gallery/bathroom" element={<Bathroom />} />
                <Route path="/gallery/wardrobe" element={<Wardrobe />} />
                <Route path="/gallery/luxury-office" element={<LuxuryOffice />} />
                <Route path="/gallery/interiors" element={<LuxuryInteriors />} />
                <Route path="/gallery/space-saving" element={<SpaceSaving />} />

                {/* Services Routes */}
                <Route path="/services/builders" element={<LuxuryBuilders />} />
                <Route path="/services/interior-design" element={<InteriorDesigning />} />
                <Route path="/services/architecture" element={<ArchitecturePlanning />} />

                {/* Discover */}
                <Route path="/stories" element={<CustomerStories />} />
                <Route path='/blogs' element={<Blogs />} />
                <Route path='/faqs' element={<FAQ />} />

                <Route path="/terms-condition" element={<TermsAndConditions />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              </Routes>

            </Layout>
          </ConsultationProvider>
        } />

      </Routes>
    </Router>
  );
}

export default App;